<script setup>
import { computed, ref } from 'vue';
import CopilotContainer from '../../copilot/CopilotContainer.vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from '../../../featureFlags';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  mobileActiveView: {
    type: String,
    default: 'list',
  },
});

const emit = defineEmits(['toggleContactPanel', 'mobileBackToConversation']);

// Mobile: show full screen when contact view is active
const isMobileContactActive = computed(() => props.isMobile && props.mobileActiveView === 'contact');

const onMobileBack = () => {
  emit('mobileBackToConversation');
};

const { t } = useI18n();

const channelType = computed(() => props.currentChat?.meta?.channel || '');

const CONTACT_TABS_OPTIONS = [
  { key: 'CONTACT', value: 'contact' },
  { key: 'COPILOT', value: 'copilot' },
];

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.map(tab => ({
    label: t(`CONVERSATION.SIDEBAR.${tab.key}`),
    value: tab.value,
  }));
});
const activeTab = ref(0);
const toggleContactPanel = () => {
  emit('toggleContactPanel');
};

const handleTabChange = selectedTab => {
  activeTab.value = tabs.value.findIndex(
    tabItem => tabItem.value === selectedTab.value
  );
};
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);
</script>

<template>
  <div
    class="ltr:border-l rtl:border-r border-n-weak overflow-hidden z-10 flex flex-col bg-n-background transition-all duration-300 ease-in-out"
    :class="[
      // Desktop: fixed width based on visibility
      !isMobile && (isVisible ? 'min-w-[320px] w-[320px] 2xl:min-w-96 2xl:w-96 h-full' : 'min-w-0 w-0 h-full'),
      // Mobile: full screen when contact view is active, hidden otherwise
      isMobile && isMobileContactActive ? 'fixed inset-0 z-50 w-full h-full border-none' : '',
      isMobile && !isMobileContactActive ? 'hidden' : '',
    ]"
  >
    <!-- Mobile header with back button -->
    <div
      v-if="isMobileContactActive"
      class="flex items-center gap-2 p-3 border-b border-n-weak bg-n-solid-1 md:hidden"
    >
      <Button
        icon="i-lucide-arrow-left"
        size="sm"
        color="slate"
        variant="ghost"
        class="!p-0 !w-8 !h-8 !min-w-8 !min-h-8 !rounded-full"
        @click="onMobileBack"
      />
      <span class="text-base font-medium text-n-slate-12">Detalhes do contato</span>
    </div>
    <div v-if="showCopilotTab && !isMobileContactActive" class="p-2">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab"
        class="w-full [&>button]:w-full"
        @tab-changed="handleTabChange"
      />
    </div>
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-if="!activeTab && currentChat.id"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
        :on-toggle="toggleContactPanel"
      />
      <CopilotContainer
        v-else-if="activeTab === 1 && showCopilotTab"
        :key="currentChat.id"
        :conversation-inbox-type="channelType"
        :conversation-id="currentChat.id"
        class="flex-1"
      />
    </div>
  </div>
</template>
